import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { Card, CardDescription, CardHeader, CardTitle } from "design-system/molecules/Card"
import { cn } from "design-system/utils"
import { ensure } from "lopermut"
import { GripVertical, MicOff, Pencil, Plus, Trash } from "lucide-react"
import { useGoalsQuery } from "modules/campaigns/hooks/sections/goals/useGoalsQuery"
import { useMemo, useState } from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { GoalsResponseDataSchema } from "schema"
import { z } from "zod"

import { Section } from "design-system/molecules/Section"
import { GetMediaElement } from "../../Sections/goals/GoalMedia"
import { GoalPickerDialog } from "../../Sections/goals/GoalPickerDialog"
import { UpsertGoalModal } from "../../Sections/goals/UpsertGoalModal"
import { DisabledSubTabMessage } from "./DisabledSubTabMessage"

type GoalContainerProps = {
  children: (dragProps: {
    attributes: ReturnType<typeof useSortable>["attributes"]
    listeners: ReturnType<typeof useSortable>["listeners"]
  }) => React.ReactNode
  className?: string
  id: string
  disabled?: boolean
}

const GoalContainer = ({ children, className, id, disabled = false }: GoalContainerProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
    transition: {
      duration: 150,
      easing: "ease",
    },
  })

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging && { zIndex: 10 }),
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "relative flex flex-col items-center w-full rounded bg-card text-base border group hover:border-border-hover",
        isDragging && "z-10",
        className,
      )}
    >
      {children({ attributes, listeners })}
    </div>
  )
}

type GoalActionSidebarProps = {
  goalIndex: number
  goalId?: string
  disabled?: boolean
  attributes: ReturnType<typeof useSortable>["attributes"]
  listeners: ReturnType<typeof useSortable>["listeners"]
  onEditGoal: (goalId: string) => void
}

const GoalActionSidebar = ({
  goalIndex,
  goalId,
  disabled,
  attributes,
  listeners,
  onEditGoal,
}: GoalActionSidebarProps) => {
  const form = useFormContext()

  return (
    <div className="flex flex-col items-center justify-start p-2 border-r min-h-16 gap-2">
      <Button
        {...attributes}
        {...listeners}
        type="button"
        variant="ghost"
        size="icon"
        className="-m-1 h-8 w-8 shrink-0 cursor-grab active:cursor-grabbing"
        disabled={disabled}
      >
        <GripVertical className="size-4" />
      </Button>
      {goalId && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="-m-1 h-8 w-8 shrink-0"
          onClick={() => onEditGoal(goalId)}
          disabled={disabled}
        >
          <Pencil className="size-4" />
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="-m-1 h-8 w-8 shrink-0"
        onClick={() => {
          const currentGoals = form.watch("goals") || []
          const newGoals = currentGoals.filter((_: unknown, i: number) => i !== goalIndex)
          form.setValue("goals", newGoals, { shouldDirty: true })
        }}
        disabled={disabled}
      >
        <Trash className="size-6" />
      </Button>
    </div>
  )
}

type Props = {
  isEditable: boolean
}

export function GoalsSubTab({ isEditable }: Props) {
  const { t } = useTranslation()
  const form = useFormContext()
  const flow = form.watch("flow")
  const { data: companyGoals } = useGoalsQuery({ query: { total: 100 } })
  const [showPickerDialog, setShowPickerDialog] = useState(false)
  const [editGoalId, setEditGoalId] = useState<string | undefined>(undefined)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  )

  const goals = form.watch("goals")
  const hasReachedMaxGoals = (goals || []).length >= 10

  const selectedGoalIds = useMemo(() => {
    return (
      goals
        ?.filter(
          (goal: unknown): goal is { id: string; isUnderlying: boolean } =>
            typeof goal === "object" && goal !== null && "id" in goal,
        )
        .map((goal: { id: string }) => goal.id) ?? []
    )
  }, [goals])

  const handleSelectGoal = (goal: z.infer<typeof GoalsResponseDataSchema>[number]) => {
    const currentGoals = form.watch("goals") || []
    if (hasReachedMaxGoals) return

    if (
      !currentGoals.some(
        (g: unknown) =>
          typeof g === "object" && g !== null && "id" in g && (g as { id: string }).id === goal.id,
      )
    ) {
      if (currentGoals.length === 1 && !currentGoals[0]) {
        form.setValue("goals", [{ id: goal.id, isUnderlying: goal.isUnderlying ?? false }], {
          shouldDirty: true,
        })
      } else {
        form.setValue(
          "goals",
          [...currentGoals, { id: goal.id, isUnderlying: goal.isUnderlying ?? false }],
          {
            shouldDirty: true,
          },
        )
      }
      form.trigger()
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const currentGoals = form.watch("goals") || []
    const activeIndex = currentGoals.findIndex(
      (_: unknown, index: number) => `goal-${index}` === active.id,
    )
    const overIndex = currentGoals.findIndex(
      (_: unknown, index: number) => `goal-${index}` === over.id,
    )

    if (activeIndex !== -1 && overIndex !== -1) {
      const reorderedGoals = [...currentGoals]
      const [removed] = reorderedGoals.splice(activeIndex, 1)
      reorderedGoals.splice(overIndex, 0, ensure(removed))
      form.setValue("goals", reorderedGoals, { shouldDirty: true })
      form.trigger()
    }
  }

  if (flow === "Inbound") {
    return <DisabledSubTabMessage message={t("campaigns.configuration.goals.inboundDisabled")} />
  }

  return (
    <div className="flex flex-col gap-4">
      <Section
        title={t("campaigns.goals.label")}
        description={t("campaigns.goals.goalsTitle")}
        action={
          goals &&
          goals.length > 0 && (
            <Button
              type="button"
              onClick={() => setShowPickerDialog(true)}
              disabled={!isEditable || hasReachedMaxGoals}
              variant="outline"
              className="w-fit"
            >
              <Plus className="size-4" />
              {t("campaigns.goals.addGoal")}
            </Button>
          )
        }
      >
        <div className="flex flex-col gap-2 w-full">
          {!goals || goals.length === 0 ? (
            <Card className="border-border border-dashed shadow-none h-60 items-center justify-center">
              <CardHeader className="text-center flex flex-col items-center gap-6 h-full justify-center">
                <div className="flex flex-col gap-2">
                  <CardTitle className="text-muted-foreground">
                    {t("campaigns.goals.noGoalsSelected.title")}
                  </CardTitle>
                  <CardDescription>
                    {t("campaigns.goals.noGoalsSelected.description")}
                  </CardDescription>
                </div>
                <Button
                  type="button"
                  onClick={() => setShowPickerDialog(true)}
                  disabled={!isEditable}
                  variant="default"
                >
                  <Plus className="size-4" />
                  {t("campaigns.goals.noGoalsSelected.cta")}
                </Button>
              </CardHeader>
            </Card>
          ) : null}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={(form.watch("goals") || []).map(
                (_: unknown, index: number) => `goal-${index}`,
              )}
              strategy={verticalListSortingStrategy}
            >
              {(form.watch("goals") || [])
                .filter(
                  (goal: unknown): goal is { id: string; isUnderlying: boolean } =>
                    typeof goal === "object" && goal !== null && "id" in goal,
                )
                .map((goal: { id: string; isUnderlying: boolean }, index: number) => {
                  const companyGoal = companyGoals?.find((g) => g.id === goal.id)
                  const isUnderlying = companyGoal?.isUnderlying ?? false
                  if (!companyGoal) return null

                  return (
                    <GoalContainer
                      key={`existing-${companyGoal.id}`}
                      id={`goal-${index}`}
                      disabled={!isEditable}
                      className="flex flex-row items-stretch w-full rounded-lg bg-card text-base border"
                    >
                      {({ attributes, listeners }) => (
                        <>
                          <GoalActionSidebar
                            goalIndex={index}
                            goalId={companyGoal.id}
                            disabled={!isEditable}
                            attributes={attributes}
                            listeners={listeners}
                            onEditGoal={() => setEditGoalId(companyGoal.id)}
                          />
                          <div className="flex flex-col items-start flex-1 p-2 gap-2">
                            <div className="flex flex-row items-center gap-1 w-full justify-between">
                              <span className="font-medium line-clamp-1">
                                {companyGoal.displayName}
                              </span>
                              <div className="flex flex-row items-center gap-2">
                                {isUnderlying && (
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <MicOff className="size-4" />
                                    </TooltipTrigger>
                                    <TooltipContent side="left">
                                      {t("campaigns.goals.underlyingDescription")}
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                                <Badge variant="outline">{companyGoal.type}</Badge>
                              </div>
                            </div>
                            {companyGoal.assistantInstructions &&
                              companyGoal.assistantInstructions.length > 0 && (
                                <span>{companyGoal.assistantInstructions}</span>
                              )}
                            {companyGoal.goalOptions.length > 0 && (
                              <div className="flex flex-col items-start gap-1 w-full">
                                <div className="flex flex-row items-center gap-2 flex-wrap">
                                  <div className="text-muted-foreground text-xs">
                                    {t("campaigns.goals.options")}
                                  </div>
                                  {companyGoal.goalOptions.map((option) => (
                                    <Badge key={option.id} variant="secondary">
                                      {option.value}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {companyGoal.goalMedias?.map((item, mediaIndex) => (
                              <div
                                key={companyGoal.id + "-" + mediaIndex + "-media"}
                                className="grid grid-cols-5 gap-2 justify-center [&>button]:w-full [&>button>div]:w-full [&>button]:justify-center [&>button]:flex"
                              >
                                <GetMediaElement key={item.link} item={item} small />
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </GoalContainer>
                  )
                })}
            </SortableContext>
          </DndContext>
        </div>
      </Section>
      <GoalPickerDialog
        open={showPickerDialog}
        onOpenChange={setShowPickerDialog}
        onSelectGoal={handleSelectGoal}
        selectedGoalIds={selectedGoalIds}
      />
      <UpsertGoalModal
        id={editGoalId}
        open={editGoalId !== undefined}
        onClose={() => setEditGoalId(undefined)}
      />
    </div>
  )
}
